import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { CheckCircle, ArrowRight, Shield, Clock, Users, Star, DollarSign, FileText, Calendar, CreditCard, FileCheck, ShieldCheck } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ContactModal from '../../components/ContactModal';
import { openChat } from '../../utils/chat';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';

const HealthcareDirectivePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const benefits = [
    {
      icon: Shield,
      title: 'Control Medical Decisions',
      description: 'Ensure your healthcare wishes are followed if you cannot speak for yourself',
    },
    {
      icon: CheckCircle,
      title: 'Reduce Family Burden',
      description: 'Prevent difficult decisions and conflicts among family members during crises',
    },
    {
      icon: DollarSign,
      title: 'Peace of Mind',
      description: 'Know your medical preferences are documented and legally enforceable',
    },
  ];

  const packages = [
    {
      name: 'Basic',
      price: '$199',
      stateFees: '',
      features: [
        'Professional consultation',
        'Document preparation',
        'Form submission',
        'Confirmation receipt',
        'Online dashboard access',
        'Basic email support',
      ],
      popular: false,
    },
    {
      name: 'Standard',
      price: '$299',
      stateFees: '',
      features: [
        'Everything in Basic',
        'Attorney review',
        'Comprehensive assistance',
        'Priority support',
        'Status monitoring',
        'Regular updates',
      ],
      popular: true,
    },
    {
      name: 'Premium',
      price: '$499',
      stateFees: '',
      features: [
        'Everything in Standard',
        'Attorney consultation',
        'Complete legal review',
        'Expedited processing',
        'Ongoing monitoring',
        '24/7 priority support',
        'Legal document templates',
      ],
      popular: false,
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Review Your Needs',
      description: 'We review your specific situation and requirements',
    },
    {
      number: '02',
      title: 'Complete the Form',
      description: 'Provide the necessary information through our simple online form',
    },
    {
      number: '03',
      title: 'We Handle Filing',
      description: 'We prepare and submit all required documents',
    },
    {
      number: '04',
      title: 'Ongoing Support',
      description: 'Receive updates and ongoing support throughout the process',
    },
  ];

  const faqs = [
    {
      question: 'What is this service?',
      answer: 'This service provides professional assistance for your legal and business needs. We handle all the paperwork and ensure everything is completed accurately and efficiently.',
    },
    {
      question: 'How long does the process take?',
      answer: 'Processing times vary depending on your specific needs and jurisdiction requirements. Most services are completed within a few weeks. We keep you updated throughout the entire process and handle any follow-up requirements.',
    },
    {
      question: 'Do I need to register in multiple classes?',
      answer: 'Our services are customized to your specific situation. We provide expert guidance to ensure comprehensive coverage and legal compliance for your needs.',
    },
    {
      question: 'What ongoing requirements exist?',
      answer: 'Regular maintenance and updates may be required depending on your service. We provide reminders and support to help you stay compliant with all requirements.',
    },
    {
      question: 'Can I use a name already in use?',
      answer: 'Not if someone else has prior rights to that mark. Our comprehensive search checks federal, state, and common law databases to identify potential conflicts before you file, saving you time and money.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Create Healthcare Directive
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Document your medical wishes and ensure your healthcare preferences are known. Professional healthcare directive services to give you and your family peace of mind.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={openChat}
                >
                  Chat Now
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={openChat}
                >
                  Chat Now</Button>
              </div>
              <div className="mt-8 flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  250K+ directives created
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  100% accuracy guarantee
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1581056771107-24ca5f033842"
                alt="Professional Service"
                className="rounded-lg shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why create a healthcare directive?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our professional service provides comprehensive support tailored to your specific needs and situation
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => (
              <Card key={idx} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <benefit.icon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
      
        </div>
      </section>

      {/* Why Choose This Service */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Image */}
            <div>
              <img
                src="https://images.unsplash.com/photo-1542387740-67856d836e59"
                alt="Service Benefits"
                className="rounded-lg shadow-xl w-full h-[500px] object-cover"
              />
            </div>

            {/* Right side - Content */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Professional legal document services
              </h2>
              
              <p className="text-base text-gray-700 mb-6 leading-relaxed">
                Our professional service provides comprehensive support tailored to your specific needs. We handle all the paperwork, ensure compliance with regulations, and guide you through every step of the process. Whether you.*re just starting out or managing an established situation, our expert team makes the process simple and stress-free.
              </p>

              <p className="text-base text-gray-700 mb-8 leading-relaxed">
                With our streamlined process, you get professional guidance and peace of mind knowing everything is handled correctly. We provide ongoing support and ensure you meet all requirements. Our affordable pricing and expert service make it easy to get started, and you can trust that your documents will be properly prepared and filed.
              </p>
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => setIsModalOpen(true)}
                >
                  Get Legal Help
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                  onClick={openChat}
                >
                  Chat Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Details Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Key service benefits
            </h2>
            <p className="text-xl text-gray-600">
              Understanding the value of professional service
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Professional Service</h3>
                <p className="text-gray-600 mb-6">
                  Our service is tailored to your specific situation and legal requirements. We ensure all documents meet state-specific legal standards and protect your interests effectively.
                </p>
                <ul className="space-y-3">
                  {[
                    'Classes 1-34 cover goods and products',
                    'Classes 35-45 cover services',
                    'Multiple class registration available',
                    'Professional classification assistance',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Expert Guidance</h3>
                <p className="text-gray-600 mb-6">
                  Choosing the right approach ensures your documents are comprehensive and allow for flexibility in addressing your needs without legal complications.
                </p>
                <ul className="space-y-3">
                  {[
                    'Cover current business activities',
                    'Plan for future product lines',
                    'Protect against competitor confusion',
                    'Maintain comprehensive coverage',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border border-gray-700">
            <h3 className="text-2xl font-bold text-white mb-4 text-center">Key service advantages</h3>
            <p className="text-base text-gray-300 text-center mb-8 max-w-4xl mx-auto">
              Our comprehensive service provides professional support and guidance. We handle all the details to ensure your needs are met efficiently and accurately.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { title: 'Legal Flicktection', items: ['Sue infringers', 'Nationwide rights', 'Presumed ownership', 'Legal advantages'] },
                { title: 'Legal Flicktection', items: ['Document validity', 'Legal compliance', 'Clear directives', 'Peace of mind'] },
                { title: 'Enforcement', items: ['Customs protection', 'Domain disputes', 'Online marketplace', 'Cease & desist'] },
                { title: 'Comprehensive Coverage', items: ['All provisions included', 'State-specific requirements', 'Professional review', 'Ongoing support'] },
              ].map((category, idx) => (
                <div key={idx}>
                  <h4 className="font-semibold text-white mb-3 text-base">{category.title}</h4>
                  <div className="space-y-2">
                    {category.items.map((item, itemIdx) => (
                      <div key={itemIdx} className="text-sm text-gray-300">
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setIsModalOpen(true)}
            >
              Get Legal Help
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={openChat}
            >
              Chat Now
            </Button>
          </div>

        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How it works
            </h2>
            <p className="text-lg text-gray-600">
              Complete process in 4 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="relative">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white text-2xl font-bold rounded-full mb-4">
                  {step.number}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-base text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
              </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Choose your service package
            </h2>
            <p className="text-xl text-gray-600">
              Select the package that best fits your needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, idx) => (
              <Card
                key={idx}
                className={`relative hover:shadow-xl transition-shadow flex flex-col ${
                  pkg.popular ? 'border-2 border-blue-600' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-8 flex flex-col flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{pkg.name}</h3>
                  <div className="mb-6">
                    <p className="text-lg text-gray-700 font-medium">Custom pricing based on your specific needs</p>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {pkg.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full mt-auto ${
                      pkg.popular
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
                    }`}
                    onClick={() => setIsModalOpen(true)}
                  >
                    Get a Quote
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
              </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What's included with our service
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Professional consultation and assessment',
              'Document preparation and review',
              'Filing with appropriate authorities',
              'Expert guidance and support',
              'Access to online tracking dashboard',
              'Customer support via phone & email',
              'Confirmation and receipt',
              'Status update notifications',
              'Ongoing assistance and updates',
            ].map((item, idx) => (
              <div key={idx} className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
              </div>
      </section>

      {/* Service Comparison */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Service comparison
            </h2>
            <p className="text-xl text-gray-600">
              Compare your options to make the best choice
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-4 text-left font-bold text-gray-900">Feature</th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-blue-600">Option A</th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">Option B</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Protects', optionA: 'Your legal rights', optionB: 'Creative works, content' },
                  { feature: 'Purpose', optionA: 'Document your wishes', optionB: 'Protect original expression' },
                  { feature: 'Registration', optionA: 'Required for federal protection', optionB: 'Automatic upon creation' },
                  { feature: 'Duration', optionA: 'Indefinite with renewals', optionB: 'Life + 70 years' },
                  { feature: 'Examples', optionA: 'Nike swoosh, McDonald\'s', optionB: 'Books, music, art' },
                  { feature: 'Scope', optionA: 'Specific goods/services', optionB: 'Entire creative work' },
                  { feature: 'Filing Office', optionA: 'Government Office', optionB: 'Alternative Office' },
                  { feature: 'Best For', optionA: 'Business identity', optionB: 'Creative content' },
                ].map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 p-4 font-semibold text-gray-900">{row.feature}</td>
                    <td className="border border-gray-300 p-4 text-center text-gray-700">{row.optionA}</td>
                    <td className="border border-gray-300 p-4 text-center text-gray-700">{row.optionB}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
              </div>
      </section>

      {/* Processing Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Service processing timeline
            </h2>
            <p className="text-lg text-gray-600">
              Understanding our service timeline
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { phase: 'Initial Review', time: '3-4 months', detail: 'Examining attorney review' },
              { phase: 'Publication', time: '30 days', detail: 'Opposition period' },
              { phase: 'Registration', time: '2-3 months', detail: 'Certificate issued' },
              { phase: 'Total Time', time: '8-12 months', detail: 'Complete process' },
              { phase: 'First Use', time: 'Variable', detail: 'Intent-to-use filing' },
              { phase: 'Office Actions', time: '6 months', detail: 'Response deadline' },
              { phase: 'Renewal', time: 'Years 5-6', detail: 'First renewal due' },
              { phase: 'Maintenance', time: 'Every 10 years', detail: 'Ongoing renewals' },
            ].map((item, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.phase}</h3>
                  <p className="text-base text-gray-600 mb-2">Timeline: {item.time}</p>
                  <p className="text-sm text-blue-600 font-semibold">{item.detail}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-base text-gray-600">
              Processing times are approximate and may vary based on workload and specific circumstances.
            </p>
          </div>
              </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              What our customers say
            </h2>
            <p className="text-lg text-blue-100">Join thousands of satisfied business owners</p>
          </div>

          <div className="relative">
            <div className="flex gap-6 animate-scroll">
              {[
                {
                  name: 'Alexandra Morrison',
                  business: 'Digital Marketing Agency',
                  content: 'Legal Flick made the process incredibly easy. The service was thorough and everything was completed without issues. Highly recommend!',
                  rating: 5,
                },
                {
                  name: 'Marcus Thompson',
                  business: 'E-commerce Entrepreneur',
                  content: 'Great service and excellent customer support. They answered all my questions and guided me through every step of the process.',
                  rating: 5,
                },
                {
                  name: 'Isabella Rodriguez',
                  business: 'Business Strategy Consultant',
                  content: 'Professional, efficient, and affordable. Everything was handled smoothly and the ongoing support provides great peace of mind.',
                  rating: 5,
                },
                {
                  name: 'James Patterson',
                  business: 'Software Development Studio',
                  content: 'The registered agent service is fantastic. They handle everything professionally and keep me informed about all important documents.',
                  rating: 5,
                },
                {
                  name: 'Sophia Chen',
                  business: 'Creative Design Agency',
                  content: 'Best decision for my business! The process was straightforward, and the support team was always available to answer my questions.',
                  rating: 5,
                },
                {
                  name: 'Daniel Foster',
                  business: 'Real Estate Investment',
                  content: 'Exceptional service from start to finish. They made the entire process hassle-free. Worth every penny!',
                  rating: 5,
                },
                {
                  name: 'Olivia Martinez',
                  business: 'Health & Wellness Coach',
                  content: 'I appreciated the transparency and guidance throughout. The dashboard makes it easy to track everything. Highly professional!',
                  rating: 5,
                },
                {
                  name: 'Ryan Anderson',
                  business: 'Mobile App Development',
                  content: 'Fast, reliable, and comprehensive. They handled all the paperwork efficiently and everything was completed without any issues.',
                  rating: 5,
                },
              ].concat([
                {
                  name: 'Alexandra Morrison',
                  business: 'Digital Marketing Agency',
                  content: 'Legal Flick made the process incredibly easy. The service was thorough and everything was completed without issues. Highly recommend!',
                  rating: 5,
                },
                {
                  name: 'Marcus Thompson',
                  business: 'E-commerce Entrepreneur',
                  content: 'Great service and excellent customer support. They answered all my questions and guided me through every step of the process.',
                  rating: 5,
                },
                {
                  name: 'Isabella Rodriguez',
                  business: 'Business Strategy Consultant',
                  content: 'Professional, efficient, and affordable. Everything was handled smoothly and the ongoing support provides great peace of mind.',
                  rating: 5,
                },
                {
                  name: 'James Patterson',
                  business: 'Software Development Studio',
                  content: 'The registered agent service is fantastic. They handle everything professionally and keep me informed about all important documents.',
                  rating: 5,
                },
              ]).map((testimonial, idx) => (
                <Card key={idx} className="flex-shrink-0 w-80 bg-white/10 backdrop-blur-sm border-white/20">
                  <CardContent className="p-6">
                    <div className="flex mb-3">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-base text-white mb-4 italic min-h-[80px]">&ldquo;{testimonial.content}&rdquo;</p>
                    <div>
                      <div className="font-semibold text-white">{testimonial.name}</div>
                      <div className="text-sm text-blue-100">{testimonial.business}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
              </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Related services
            </h2>
            <p className="text-lg text-gray-600">
              Additional services to help your business succeed
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Registered Agent Service',
                description: 'Professional registered agent service to handle your legal documents and ensure you never miss important notices',
              },
              {
                title: 'Business Licenses',
                description: 'Get help obtaining all required federal, state, and local licenses for your business operations',
              },
              {
                title: 'EIN (Tax ID Number)',
                description: 'Professional document review and notarization services',
              },
            ].map((service, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-base text-gray-600 mb-6">{service.description}</p>
                  <Button variant="outline" className="w-full border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={() => setIsModalOpen(true)}>
                    Create Healthcare Directive
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
              </div>
      </section>

      {/* Expert Tips */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Expert tips for this service
            </h2>
            <p className="text-lg text-gray-600">Follow these best practices to protect and grow your business</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: DollarSign,
                title: 'Use Your Mark Consistently',
                description: 'Always maintain consistent documentation of your activities. Keep detailed records with dates and examples to support your case if needed.',
              },
              {
                icon: FileText,
                title: 'Monitor for Infringement',
                description: 'Review and update your documents regularly, especially after major life changes or legal updates.',
              },
              {
                icon: Calendar,
                title: 'Track Renewal Deadlines',
                description: 'Regular maintenance may be required. Set reminders well in advance to ensure you meet all deadlines and requirements.',
              },
              {
                icon: CreditCard,
                title: 'Expand Strategically',
                description: 'Consider expanding your coverage as your needs grow. File additional documents for new situations to maintain comprehensive protection.',
              },
              {
                icon: FileCheck,
                title: 'Document Everything',
                description: 'Keep all original documents in a secure location and provide copies to relevant parties as needed.',
              },
              {
                icon: ShieldCheck,
                title: 'Protect Your Interests',
                description: 'Stay proactive in protecting your interests. Regular monitoring and timely action help ensure your rights are maintained over time.',
              },
            ].map((tip, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <tip.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{tip.title}</h3>
                      <p className="text-base text-gray-600">{tip.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setIsModalOpen(true)}
            >
              Get Legal Help
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={openChat}
            >
              Chat Now
            </Button>
          </div>

        </div>
      </section>

      {/* Why Choose Legal Flick */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why choose Legal Flick for this service?
            </h2>
            <p className="text-xl text-gray-600">
              Industry-leading service backed by expertise and technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                number: '100K+',
                label: 'Services Provided',
                description: 'Trusted by businesses nationwide',
              },
              {
                number: '99%',
                label: 'Approval Rate',
                description: 'Industry-leading filing accuracy',
              },
              {
                number: '24/7',
                label: 'Support Available',
                description: 'Get help whenever you need it',
              },
              {
                number: '$199',
                label: 'Starting Cost',
                description: 'Affordable packages for every budget',
              },
            ].map((stat, idx) => (
              <Card key={idx} className="text-center hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                  <div className="font-semibold text-gray-900 mb-2">{stat.label}</div>
                  <div className="text-sm text-gray-600">{stat.description}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-8">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">100% Accuracy Guarantee</h3>
                <p className="text-gray-600 text-sm">
                  If we make an error, we will correct it at no additional cost to you
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Fast Processing</h3>
                <p className="text-gray-600 text-sm">
                  Most filings completed within 5-10 business days, with expedited options available
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Dedicated Support Team</h3>
                <p className="text-gray-600 text-sm">
                  Expert guidance from business specialists throughout the entire process
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Common Questions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Common questions answered
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                question: 'Can I be my own registered agent?',
                answer: 'Yes, but we recommend using a professional registered agent service. They ensure you never miss important legal documents and provide privacy by keeping your home address off public records.',
              },
              {
                question: 'How much does this service cost?',
                answer: 'Our service packages are priced based on your specific needs. Contact us for detailed pricing information tailored to your situation.',
              },
              {
                question: 'Do I need an attorney to file?',
                answer: 'While not required, professional assistance improves your chances of approval and helps avoid costly mistakes. Our service includes expert guidance throughout the process.',
              },
              {
                question: 'What can I include in this service?',
                answer: 'Yes, you can include various provisions and customize the document to fit your specific needs. Each requires separate registration.',
              },
              {
                question: 'What if there are complications?',
                answer: 'During the 30-day publication period, others can file an opposition. We provide guidance on responding to oppositions and protecting your application.',
              },
              {
                question: 'Can this service work internationally?',
                answer: 'Yes, we can assist with international requirements and help you navigate cross-border processes. Contact us to discuss your specific international needs.',
              },
            ].map((item, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{item.question}</h3>
                  <p className="text-gray-600">{item.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setIsModalOpen(true)}
            >
              Create Healthcare Directive
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={openChat}
            >
              Chat Now
            </Button>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700"></div>
        
        {/* Overlay Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block px-6 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-semibold mb-6">
              🎉 Limited Time Offer
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-white">
              Ready to Create Your Healthcare Directive?
            </h2>
            
            <p className="text-xl lg:text-2xl mb-4 text-blue-100 max-w-3xl mx-auto">
              Join thousands of satisfied customers who trust Legal Flick for their legal needs
            </p>
            
            <p className="text-lg mb-10 text-blue-50 max-w-2xl mx-auto">
              Create your healthcare directive today to ensure your medical wishes are known. Make important healthcare decisions in advance and give your loved ones clear guidance.
            </p>

            

            <div className="grid md:grid-cols-3 gap-8 max-w-3xl mx-auto text-center">
              <div className="backdrop-blur-sm bg-white/10 rounded-lg p-4">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="w-6 h-6 text-green-300 mr-2" />
                  <span className="text-white font-semibold">100% Accuracy</span>
                </div>
                <p className="text-blue-100 text-sm">Guaranteed filing accuracy</p>
              </div>
              
              <div className="backdrop-blur-sm bg-white/10 rounded-lg p-4">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="w-6 h-6 text-green-300 mr-2" />
                  <span className="text-white font-semibold">Fast Processing</span>
                </div>
                <p className="text-blue-100 text-sm">Most orders completed in 5-10 days</p>
              </div>
              
              <div className="backdrop-blur-sm bg-white/10 rounded-lg p-4">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="w-6 h-6 text-green-300 mr-2" />
                  <span className="text-white font-semibold">Expert Support</span>
                </div>
                <p className="text-blue-100 text-sm">24/7 customer assistance</p>
              </div>
            </div>
          </div>
              </div>
      </section>

      {/* Service Details Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Key service benefits
            </h2>
            <p className="text-xl text-gray-600">
              Understanding the value of professional service
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Professional Service</h3>
                <p className="text-gray-600 mb-6">
                  Our service is tailored to your specific situation and legal requirements. We ensure all documents meet state-specific legal standards and protect your interests effectively.
                </p>
                <ul className="space-y-3">
                  {[
                    'Classes 1-34 cover goods and products',
                    'Classes 35-45 cover services',
                    'Multiple class registration available',
                    'Professional classification assistance',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Expert Guidance</h3>
                <p className="text-gray-600 mb-6">
                  Choosing the right approach ensures your documents are comprehensive and allow for flexibility in addressing your needs without legal complications.
                </p>
                <ul className="space-y-3">
                  {[
                    'Cover current business activities',
                    'Plan for future product lines',
                    'Protect against competitor confusion',
                    'Maintain comprehensive coverage',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border border-gray-700">
            <h3 className="text-2xl font-bold text-white mb-4 text-center">Key service advantages</h3>
            <p className="text-base text-gray-300 text-center mb-8 max-w-4xl mx-auto">
              Our comprehensive service provides professional support and guidance. We handle all the details to ensure your needs are met efficiently and accurately.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { title: 'Legal Flicktection', items: ['Sue infringers', 'Nationwide rights', 'Presumed ownership', 'Legal advantages'] },
                { title: 'Legal Flicktection', items: ['Document validity', 'Legal compliance', 'Clear directives', 'Peace of mind'] },
                { title: 'Enforcement', items: ['Customs protection', 'Domain disputes', 'Online marketplace', 'Cease & desist'] },
                { title: 'Comprehensive Coverage', items: ['All provisions included', 'State-specific requirements', 'Professional review', 'Ongoing support'] },
              ].map((category, idx) => (
                <div key={idx}>
                  <h4 className="font-semibold text-white mb-3 text-base">{category.title}</h4>
                  <div className="space-y-2">
                    {category.items.map((item, itemIdx) => (
                      <div key={itemIdx} className="text-sm text-gray-300">
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
              </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How it works
            </h2>
            <p className="text-lg text-gray-600">
              Complete process in 4 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="relative">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 text-white text-2xl font-bold rounded-full mb-4">
                  {step.number}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-base text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
              </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Choose your service package
            </h2>
            <p className="text-xl text-gray-600">
              Select the package that best fits your needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, idx) => (
              <Card
                key={idx}
                className={`relative hover:shadow-xl transition-shadow flex flex-col ${
                  pkg.popular ? 'border-2 border-blue-600' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-8 flex flex-col flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{pkg.name}</h3>
                  <div className="mb-6">
                    <p className="text-lg text-gray-700 font-medium">Custom pricing based on your specific needs</p>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {pkg.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full mt-auto ${
                      pkg.popular
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
                    }`}
                    onClick={() => setIsModalOpen(true)}
                  >
                    Get a Quote
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
              </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              What's included with our service
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Professional consultation and assessment',
              'Document preparation and review',
              'Filing with appropriate authorities',
              'Expert guidance and support',
              'Access to online tracking dashboard',
              'Customer support via phone & email',
              'Confirmation and receipt',
              'Status update notifications',
              'Ongoing assistance and updates',
            ].map((item, idx) => (
              <div key={idx} className="flex items-start">
                <CheckCircle className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
              </div>
      </section>

      {/* Service Comparison */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Service comparison
            </h2>
            <p className="text-xl text-gray-600">
              Compare your options to make the best choice
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-4 text-left font-bold text-gray-900">Feature</th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-blue-600">Option A</th>
                  <th className="border border-gray-300 p-4 text-center font-bold text-gray-900">Option B</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Protects', optionA: 'Your legal rights', optionB: 'Creative works, content' },
                  { feature: 'Purpose', optionA: 'Document your wishes', optionB: 'Protect original expression' },
                  { feature: 'Registration', optionA: 'Required for federal protection', optionB: 'Automatic upon creation' },
                  { feature: 'Duration', optionA: 'Indefinite with renewals', optionB: 'Life + 70 years' },
                  { feature: 'Examples', optionA: 'Nike swoosh, McDonald\'s', optionB: 'Books, music, art' },
                  { feature: 'Scope', optionA: 'Specific goods/services', optionB: 'Entire creative work' },
                  { feature: 'Filing Office', optionA: 'Government Office', optionB: 'Alternative Office' },
                  { feature: 'Best For', optionA: 'Business identity', optionB: 'Creative content' },
                ].map((row, idx) => (
                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 p-4 font-semibold text-gray-900">{row.feature}</td>
                    <td className="border border-gray-300 p-4 text-center text-gray-700">{row.optionA}</td>
                    <td className="border border-gray-300 p-4 text-center text-gray-700">{row.optionB}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
              </div>
      </section>

      {/* Processing Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Service processing timeline
            </h2>
            <p className="text-lg text-gray-600">
              Understanding our service timeline
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { phase: 'Initial Review', time: '3-4 months', detail: 'Examining attorney review' },
              { phase: 'Publication', time: '30 days', detail: 'Opposition period' },
              { phase: 'Registration', time: '2-3 months', detail: 'Certificate issued' },
              { phase: 'Total Time', time: '8-12 months', detail: 'Complete process' },
              { phase: 'First Use', time: 'Variable', detail: 'Intent-to-use filing' },
              { phase: 'Office Actions', time: '6 months', detail: 'Response deadline' },
              { phase: 'Renewal', time: 'Years 5-6', detail: 'First renewal due' },
              { phase: 'Maintenance', time: 'Every 10 years', detail: 'Ongoing renewals' },
            ].map((item, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.phase}</h3>
                  <p className="text-base text-gray-600 mb-2">Timeline: {item.time}</p>
                  <p className="text-sm text-blue-600 font-semibold">{item.detail}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-base text-gray-600">
              Processing times are approximate and may vary based on workload and specific circumstances.
            </p>
          </div>
              </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              What our customers say
            </h2>
            <p className="text-lg text-blue-100">Join thousands of satisfied business owners</p>
          </div>

          <div className="relative">
            <div className="flex gap-6 animate-scroll">
              {[
                {
                  name: 'Alexandra Morrison',
                  business: 'Digital Marketing Agency',
                  content: 'Legal Flick made the process incredibly easy. The service was thorough and everything was completed without issues. Highly recommend!',
                  rating: 5,
                },
                {
                  name: 'Marcus Thompson',
                  business: 'E-commerce Entrepreneur',
                  content: 'Great service and excellent customer support. They answered all my questions and guided me through every step of the process.',
                  rating: 5,
                },
                {
                  name: 'Isabella Rodriguez',
                  business: 'Business Strategy Consultant',
                  content: 'Professional, efficient, and affordable. Everything was handled smoothly and the ongoing support provides great peace of mind.',
                  rating: 5,
                },
                {
                  name: 'James Patterson',
                  business: 'Software Development Studio',
                  content: 'The registered agent service is fantastic. They handle everything professionally and keep me informed about all important documents.',
                  rating: 5,
                },
                {
                  name: 'Sophia Chen',
                  business: 'Creative Design Agency',
                  content: 'Best decision for my business! The process was straightforward, and the support team was always available to answer my questions.',
                  rating: 5,
                },
                {
                  name: 'Daniel Foster',
                  business: 'Real Estate Investment',
                  content: 'Exceptional service from start to finish. They made the entire process hassle-free. Worth every penny!',
                  rating: 5,
                },
                {
                  name: 'Olivia Martinez',
                  business: 'Health & Wellness Coach',
                  content: 'I appreciated the transparency and guidance throughout. The dashboard makes it easy to track everything. Highly professional!',
                  rating: 5,
                },
                {
                  name: 'Ryan Anderson',
                  business: 'Mobile App Development',
                  content: 'Fast, reliable, and comprehensive. They handled all the paperwork efficiently and everything was completed without any issues.',
                  rating: 5,
                },
              ].concat([
                {
                  name: 'Alexandra Morrison',
                  business: 'Digital Marketing Agency',
                  content: 'Legal Flick made the process incredibly easy. The service was thorough and everything was completed without issues. Highly recommend!',
                  rating: 5,
                },
                {
                  name: 'Marcus Thompson',
                  business: 'E-commerce Entrepreneur',
                  content: 'Great service and excellent customer support. They answered all my questions and guided me through every step of the process.',
                  rating: 5,
                },
                {
                  name: 'Isabella Rodriguez',
                  business: 'Business Strategy Consultant',
                  content: 'Professional, efficient, and affordable. Everything was handled smoothly and the ongoing support provides great peace of mind.',
                  rating: 5,
                },
                {
                  name: 'James Patterson',
                  business: 'Software Development Studio',
                  content: 'The registered agent service is fantastic. They handle everything professionally and keep me informed about all important documents.',
                  rating: 5,
                },
              ]).map((testimonial, idx) => (
                <Card key={idx} className="flex-shrink-0 w-80 bg-white/10 backdrop-blur-sm border-white/20">
                  <CardContent className="p-6">
                    <div className="flex mb-3">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-base text-white mb-4 italic min-h-[80px]">&ldquo;{testimonial.content}&rdquo;</p>
                    <div>
                      <div className="font-semibold text-white">{testimonial.name}</div>
                      <div className="text-sm text-blue-100">{testimonial.business}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
              </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Related services
            </h2>
            <p className="text-lg text-gray-600">
              Additional services to help your business succeed
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Registered Agent Service',
                description: 'Professional registered agent service to handle your legal documents and ensure you never miss important notices',
              },
              {
                title: 'Business Licenses',
                description: 'Get help obtaining all required federal, state, and local licenses for your business operations',
              },
              {
                title: 'EIN (Tax ID Number)',
                description: 'Professional document review and notarization services',
              },
            ].map((service, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-base text-gray-600 mb-6">{service.description}</p>
                  <Button variant="outline" className="w-full border-blue-600 text-blue-600 hover:bg-blue-50"
                onClick={() => setIsModalOpen(true)}>
                    Create Healthcare Directive
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
              </div>
      </section>

      {/* Expert Tips */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Expert tips for this service
            </h2>
            <p className="text-lg text-gray-600">Follow these best practices to protect and grow your business</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: DollarSign,
                title: 'Use Your Mark Consistently',
                description: 'Always maintain consistent documentation of your activities. Keep detailed records with dates and examples to support your case if needed.',
              },
              {
                icon: FileText,
                title: 'Monitor for Infringement',
                description: 'Review and update your documents regularly, especially after major life changes or legal updates.',
              },
              {
                icon: Calendar,
                title: 'Track Renewal Deadlines',
                description: 'Regular maintenance may be required. Set reminders well in advance to ensure you meet all deadlines and requirements.',
              },
              {
                icon: CreditCard,
                title: 'Expand Strategically',
                description: 'Consider expanding your coverage as your needs grow. File additional documents for new situations to maintain comprehensive protection.',
              },
              {
                icon: FileCheck,
                title: 'Document Everything',
                description: 'Keep all original documents in a secure location and provide copies to relevant parties as needed.',
              },
              {
                icon: ShieldCheck,
                title: 'Protect Your Interests',
                description: 'Stay proactive in protecting your interests. Regular monitoring and timely action help ensure your rights are maintained over time.',
              },
            ].map((tip, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <tip.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{tip.title}</h3>
                      <p className="text-base text-gray-600">{tip.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setIsModalOpen(true)}
            >
              Get Legal Help
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={openChat}
            >
              Chat Now
            </Button>
          </div>

        </div>
      </section>

      {/* Why Choose Legal Flick */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why choose Legal Flick for this service?
            </h2>
            <p className="text-xl text-gray-600">
              Industry-leading service backed by expertise and technology
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                number: '100K+',
                label: 'Services Provided',
                description: 'Trusted by businesses nationwide',
              },
              {
                number: '99%',
                label: 'Approval Rate',
                description: 'Industry-leading filing accuracy',
              },
              {
                number: '24/7',
                label: 'Support Available',
                description: 'Get help whenever you need it',
              },
              {
                number: '$199',
                label: 'Starting Cost',
                description: 'Affordable packages for every budget',
              },
            ].map((stat, idx) => (
              <Card key={idx} className="text-center hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                  <div className="font-semibold text-gray-900 mb-2">{stat.label}</div>
                  <div className="text-sm text-gray-600">{stat.description}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-8">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">100% Accuracy Guarantee</h3>
                <p className="text-gray-600 text-sm">
                  If we make an error, we will correct it at no additional cost to you
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Fast Processing</h3>
                <p className="text-gray-600 text-sm">
                  Most filings completed within 5-10 business days, with expedited options available
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Dedicated Support Team</h3>
                <p className="text-gray-600 text-sm">
                  Expert guidance from business specialists throughout the entire process
                </p>
              </CardContent>
            </Card>
          </div>
      
        </div>
      </section>

      {/* Common Questions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Common questions answered
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                question: 'Can I be my own registered agent?',
                answer: 'Yes, but we recommend using a professional registered agent service. They ensure you never miss important legal documents and provide privacy by keeping your home address off public records.',
              },
              {
                question: 'How much does this service cost?',
                answer: 'Our service packages are priced based on your specific needs. Contact us for detailed pricing information tailored to your situation.',
              },
              {
                question: 'Do I need an attorney to file?',
                answer: 'While not required, professional assistance improves your chances of approval and helps avoid costly mistakes. Our service includes expert guidance throughout the process.',
              },
              {
                question: 'What can I include in this service?',
                answer: 'Yes, you can include various provisions and customize the document to fit your specific needs. Each requires separate registration.',
              },
              {
                question: 'What if there are complications?',
                answer: 'During the 30-day publication period, others can file an opposition. We provide guidance on responding to oppositions and protecting your application.',
              },
              {
                question: 'Can this service work internationally?',
                answer: 'Yes, we can assist with international requirements and help you navigate cross-border processes. Contact us to discuss your specific international needs.',
              },
            ].map((item, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{item.question}</h3>
                  <p className="text-gray-600">{item.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setIsModalOpen(true)}
            >
              Create Healthcare Directive
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
              onClick={openChat}
            >
              Chat Now
            </Button>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700"></div>
        
        {/* Overlay Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block px-6 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white font-semibold mb-6">
              🎉 Limited Time Offer
            </div>
            
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-white">
              Ready to Get Legal Help?
            </h2>
            
            <p className="text-xl lg:text-2xl mb-4 text-blue-100 max-w-3xl mx-auto">
              Join thousands of satisfied customers who trust Legal Flick for their legal needs
            </p>
            
            <p className="text-lg mb-10 text-blue-50 max-w-2xl mx-auto">
              Create your healthcare directive today to ensure your medical wishes are known. Make important healthcare decisions in advance and give your loved ones clear guidance.
            </p>

            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-10">
              <Button
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-10 py-6 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                onClick={() => setIsModalOpen(true)}
              >
                Create Healthcare Directive
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6"
                onClick={openChat}
              >
                  Chat Now
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-3xl mx-auto text-center">
              <div className="backdrop-blur-sm bg-white/10 rounded-lg p-4">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="w-6 h-6 text-green-300 mr-2" />
                  <span className="text-white font-semibold">100% Accuracy</span>
                </div>
                <p className="text-blue-100 text-sm">Guaranteed filing accuracy</p>
              </div>
              
              <div className="backdrop-blur-sm bg-white/10 rounded-lg p-4">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="w-6 h-6 text-green-300 mr-2" />
                  <span className="text-white font-semibold">Fast Processing</span>
                </div>
                <p className="text-blue-100 text-sm">Most orders completed in 5-10 days</p>
              </div>
              
              <div className="backdrop-blur-sm bg-white/10 rounded-lg p-4">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="w-6 h-6 text-green-300 mr-2" />
                  <span className="text-white font-semibold">Expert Support</span>
                </div>
                <p className="text-blue-100 text-sm">24/7 customer assistance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default HealthcareDirectivePage;